// src/components/Scanner.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, AlertCircle, X } from 'lucide-react';
import DropZone from './DropZone';
import ScanButton from './ScanButton';
import ResultsBanner from './ResultsBanner';
import FileInfoCard from './FileInfoCard';
import Top3Card from './Top3Card';
import VirusTotalCard from './VirusTotalCard';
import EntropyCard from './EntropyCard';
import YaraCard from './YaraCard';
import { api, getApiErrorMessage, ScanResult } from '../services/api';
import './Scanner.css';

interface ScannerProps {
  apiStatus: 'online' | 'offline' | 'checking';
}

const Scanner: React.FC<ScannerProps> = ({ apiStatus }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [scanMessage, setScanMessage] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);

  const messages = [
    'Converting binary to image...',
    'Running CNN analysis...',
    'Querying VirusTotal...',
    'Generating YARA rule...',
  ];

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setResult(null);
    setScanError(null);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setResult(null);
    setScanError(null);
  };

  const handleScan = async () => {
    if (!selectedFile || apiStatus !== 'online') return;

    setScanning(true);
    setResult(null);
    setScanError(null);

    let messageIndex = 0;
    const interval = setInterval(() => {
      setScanMessage(messages[messageIndex % messages.length]);
      messageIndex++;
    }, 2000);

    try {
      const scanResult = await api.scanComplete(selectedFile);
      setResult(scanResult);
    } catch (error) {
      console.error('Scan failed:', error);
      setScanError(getApiErrorMessage(error));
    } finally {
      clearInterval(interval);
      setScanning(false);
      setScanMessage('');
    }
  };

  return (
    <section className="scanner-section" id="scanner">
      <div className="scanner-container">
        <h2 className="section-title">Try it yourself</h2>

        <div className="scanner-grid">
          {/* Left side - Upload Panel */}
          <div className="upload-panel">
            <DropZone onFileSelect={handleFileSelect} disabled={scanning} />

            {/* File Preview */}
            {selectedFile && !scanning && (
              <div className="file-preview">
                <div className="file-preview-content">
                  <span className="file-preview-name">{selectedFile.name}</span>
                  <span className="file-preview-size">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </span>
                  <button className="remove-file" onClick={handleRemoveFile}>
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}

            <div className="test-sample">
              <Download size={18} className="download-icon" />
              <span className="test-text">Don't have a file? Use our test sample</span>
              <a
                href="/eicar.exe"
                download="eicar_test.exe"
                className="test-button"
              >
                Download EICAR test file
              </a>
            </div>

            <div className="file-limit-note">
              <AlertCircle size={14} />
              <span>Max 50MB · .exe, .dll, .bin, .com, .sys supported</span>
            </div>

            <ScanButton
              onClick={handleScan}
              disabled={!selectedFile || scanning || apiStatus !== 'online'}
            />

            {scanning && (
              <div className="scanning-status">
                <div className="spinner"></div>
                <span>{scanMessage || 'Analyzing...'}</span>
              </div>
            )}

            {scanError && (
              <div className="dropzone-error" role="alert">
                <AlertCircle size={16} />
                <span>{scanError}</span>
              </div>
            )}
          </div>

          {/* Right side - Results Panel */}
          <AnimatePresence>
            {result && (
              <motion.div
                className="results-panel"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
              >
                <ResultsBanner verdict={result.cnn_verdict} />

                <div className="results-grid">
                  {/* Updated: Added threatLevel prop to these three components */}
                  <FileInfoCard 
                    file={result.file} 
                    threatLevel={result.cnn_verdict.threat_level} 
                  />
                  <Top3Card 
                    predictions={result.top3} 
                    threatLevel={result.cnn_verdict.threat_level} 
                  />
                  <VirusTotalCard vtData={result.virustotal} />
                  <EntropyCard 
                    entropy={result.file.entropy} 
                    threatLevel={result.cnn_verdict.threat_level} 
                  />
                </div>

                <YaraCard yaraData={result.yara} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Scanner;
